/* r4-meganav.js
   Scoped, dependency-free behaviour for the beauty-brand mega menu. No WebGL,
   no global leaks (everything lives inside this IIFE). Works progressively:
   if this never runs, the first panel is visible and every nav link still
   navigates to its collection.

   DESKTOP: hovering a primary nav link or the panel opens the mega menu
   pre-selected to the matching tab. Hover-intent delay (~150ms) before close
   prevents flicker. Escape closes, and so does focus leaving the header
   (keyboard users are not left with the panel stuck open). Tabs are real
   buttons (Enter / Space / arrow keys), set aria-selected, and swap panels.

   MOBILE: the same tab logic drives the drawer's horizontal tab row and the
   active tab's vertical card list.

   RE-INIT: all per-node bindings live in init functions that run again on
   shopify:section:load, so the nav keeps working after the theme editor
   re-renders the header section with fresh DOM (the same failure class as the
   .reveal editor bug). Document-level listeners are bound exactly once and
   look nodes up at event time, so they never double-fire or go stale. */
(function () {
  "use strict";

  var CLOSE_DELAY = 150;
  var closeTimer = null;

  /* ---------- Desktop mega menu ---------- */
  function initDesktopMega() {
    var header = document.getElementById("siteHeader");
    var mega = document.getElementById("r4Mega");
    if (!header || !mega) return;
    if (mega.dataset.dqMegaInit === "1") return; // this DOM instance already bound
    mega.dataset.dqMegaInit = "1";

    var tabs = Array.prototype.slice.call(mega.querySelectorAll("[data-mega-tab]"));
    var panels = Array.prototype.slice.call(mega.querySelectorAll("[data-mega-panel]"));
    var navLinks = Array.prototype.slice.call(header.querySelectorAll("[data-mega-link]"));
    var region = header.querySelector(".site-header__inset");
    // The classic dropdown (2026-07-14) has no tabs/panels, just an open/close sheet;
    // only the hover region is required. Tab wiring below self-skips on empty arrays.
    if (!region) return;

    function selectTab(key) {
      tabs.forEach(function (tab) {
        var on = tab.getAttribute("data-mega-tab") === key;
        tab.setAttribute("aria-selected", on ? "true" : "false");
        tab.tabIndex = on ? 0 : -1;
        tab.classList.toggle("is-active", on);
      });
      panels.forEach(function (panel) {
        var on = panel.getAttribute("data-mega-panel") === key;
        panel.classList.toggle("is-active", on);
        if (on) { panel.removeAttribute("hidden"); } else { panel.setAttribute("hidden", ""); }
      });
    }

    function open(key) {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      if (key) selectTab(key);
      header.classList.add("mega-open");
      mega.setAttribute("aria-hidden", "false");
    }

    var editorHold = false; // a dropdown block is selected in the theme editor
    function close() {
      if (editorHold) return;
      header.classList.remove("mega-open");
      mega.setAttribute("aria-hidden", "true");
    }

    function scheduleClose() {
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(close, CLOSE_DELAY);
    }

    function cancelClose() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    }

    /* Hover a primary nav link: open pre-selected to its tab. */
    navLinks.forEach(function (link) {
      link.addEventListener("mouseenter", function () {
        open(link.getAttribute("data-mega-link"));
      });
      link.addEventListener("mouseleave", scheduleClose);
      /* Keyboard users: focusing a link reveals the menu too. */
      link.addEventListener("focus", function () {
        open(link.getAttribute("data-mega-link"));
      });
    });

    /* Keep open while the cursor is over the island region or the panel. */
    region.addEventListener("mouseenter", cancelClose);
    region.addEventListener("mouseleave", scheduleClose);
    mega.addEventListener("mouseenter", cancelClose);
    mega.addEventListener("mouseleave", scheduleClose);

    /* THEME EDITOR: selecting a dropdown block (collection link, page link) must SHOW
       the dropdown, else the click highlights nothing and the block looks uneditable.
       editorHold pins it open so hover-close cannot snap it shut while editing. */
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener("shopify:block:select", function (e) {
        if (mega.contains(e.target)) { editorHold = true; cancelClose(); open(); }
      });
      document.addEventListener("shopify:block:deselect", function (e) {
        if (mega.contains(e.target)) { editorHold = false; close(); }
      });
    }

    /* Tabs: hover and click both switch; click also keeps the menu open. */
    tabs.forEach(function (tab, index) {
      var key = tab.getAttribute("data-mega-tab");
      tab.addEventListener("mouseenter", function () { open(key); });
      tab.addEventListener("click", function () { open(key); });
      tab.addEventListener("keydown", function (e) {
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
          e.preventDefault();
          var dir = e.key === "ArrowRight" ? 1 : -1;
          var next = (index + dir + tabs.length) % tabs.length;
          selectTab(tabs[next].getAttribute("data-mega-tab"));
          open();
          tabs[next].focus();
        } else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open(key);
        }
      });
    });
  }

  /* ---------- Mobile mega (inside the drawer) ---------- */
  function initMobileMega() {
    var root = document.querySelector("[data-mmega]");
    if (!root) return;
    if (root.dataset.dqMmegaInit === "1") return;
    root.dataset.dqMmegaInit = "1";
    var tabs = Array.prototype.slice.call(root.querySelectorAll("[data-mmega-tab]"));
    var panels = Array.prototype.slice.call(root.querySelectorAll("[data-mmega-panel]"));
    if (!tabs.length || !panels.length) return;

    function select(key) {
      tabs.forEach(function (tab) {
        var on = tab.getAttribute("data-mmega-tab") === key;
        tab.setAttribute("aria-selected", on ? "true" : "false");
        tab.tabIndex = on ? 0 : -1;
        tab.classList.toggle("is-active", on);
      });
      panels.forEach(function (panel) {
        var on = panel.getAttribute("data-mmega-panel") === key;
        panel.classList.toggle("is-active", on);
        if (on) { panel.removeAttribute("hidden"); } else { panel.setAttribute("hidden", ""); }
      });
    }

    tabs.forEach(function (tab, index) {
      var key = tab.getAttribute("data-mmega-tab");
      tab.addEventListener("click", function () { select(key); });
      tab.addEventListener("keydown", function (e) {
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
          e.preventDefault();
          var dir = e.key === "ArrowRight" ? 1 : -1;
          var next = (index + dir + tabs.length) % tabs.length;
          select(tabs[next].getAttribute("data-mmega-tab"));
          tabs[next].focus();
        }
      });
    });
  }

  function initAll() {
    initDesktopMega();
    initMobileMega();
  }

  initAll();

  /* Re-bind when the theme editor re-renders the header (fresh DOM = fresh init;
     the data-dq*Init flags live on the replaced nodes so re-init is safe). */
  document.addEventListener("shopify:section:load", initAll);
  document.addEventListener("shopify:section:select", initAll);

  /* Document-level closers, bound once, stateless lookups. */
  function closeMegaNow() {
    var header = document.getElementById("siteHeader");
    var mega = document.getElementById("r4Mega");
    if (header) header.classList.remove("mega-open");
    if (mega) mega.setAttribute("aria-hidden", "true");
  }

  /* Escape closes and returns focus to the page. */
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    var header = document.getElementById("siteHeader");
    if (header && header.classList.contains("mega-open")) closeMegaNow();
  });

  /* Keyboard users: when focus moves outside the header and the open panel,
     close the mega menu instead of leaving it covering the page. */
  document.addEventListener("focusin", function (e) {
    var header = document.getElementById("siteHeader");
    if (!header || !header.classList.contains("mega-open")) return;
    var mega = document.getElementById("r4Mega");
    if (header.contains(e.target) || (mega && mega.contains(e.target))) return;
    closeMegaNow();
  });
})();
